// src/components/SalvationArmyBadge.js
//
// "Received help from Salvation Army" marker. The flag is deliberately a
// rolling 12-month one: staff record the date the help was received, and the
// badge disappears on its own a year later instead of relying on someone
// remembering to clear the checkbox. The stored flag/date are left alone so
// the history stays on the record.

import React from "react";

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

/** Parse a "YYYY-MM-DD" (or ISO datetime) value as a local date. */
const parseDate = (val) => {
  if (!val) return null;
  const [y, m, d] = String(val).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

/**
 * True when the client is flagged AND the help was received within the last
 * year. A flag with no date recorded still shows: the box was checked on
 * purpose, and hiding it outright would lose the information entirely.
 */
export function salvationArmyHelpIsCurrent(client) {
  if (!client?.salvation_army_help) return false;
  const when = parseDate(client.salvation_army_help_date);
  if (!when) return true;
  return Date.now() - when.getTime() <= ONE_YEAR_MS;
}

/** "Received help from Salvation Army — 3/14/2026" for the hover title. */
export function salvationArmyHelpTitle(client) {
  const when = parseDate(client?.salvation_army_help_date);
  return when
    ? `Received help from Salvation Army — ${when.toLocaleDateString()}`
    : "Received help from Salvation Army";
}

/** The Salvation Army red shield, drawn inline so it scales cleanly. */
function RedShield({ size }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      role="img"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M4 3h24v13.5c0 6.4-4.6 10.9-12 12.5C8.6 27.4 4 22.9 4 16.5V3z"
        fill="#ED1B2E"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <text
        x="16"
        y="20.5"
        textAnchor="middle"
        fill="#ffffff"
        fontSize="15"
        fontWeight="700"
        fontFamily="Georgia, 'Times New Roman', serif"
      >
        S
      </text>
    </svg>
  );
}

/**
 * Renders nothing unless the flag is set and still inside its 12-month window,
 * so callers can drop it in next to a name without guarding first.
 */
export default function SalvationArmyBadge({ client, size = 24, className = "" }) {
  if (!salvationArmyHelpIsCurrent(client)) return null;

  return (
    <span
      className={`d-inline-flex align-items-center ${className}`.trim()}
      title={salvationArmyHelpTitle(client)}
      style={{ cursor: "default", lineHeight: 0 }}
    >
      <RedShield size={size} />
      <span className="visually-hidden">{salvationArmyHelpTitle(client)}</span>
    </span>
  );
}
